import { RotateCcw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RangeFilter } from "@/components/RangeFilter";
import { AGE_RANGE, HEIGHT_RANGE, SPORTS, fmtHeight } from "@/constants/sports";
import { AttributeFilter } from "@/components/AttributeFilter";

export const DEFAULT_FILTERS = { sport: "all", position: "all", team: "all", class_year: "all", division: "all", position_group: "all", attrs: [], age: AGE_RANGE, height: HEIGHT_RANGE };

const SPORT_OPTIONS = [["all", "All"], ...Object.entries(SPORTS).map(([k, s]) => [k, s.label])];

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div>
    <span className="eyebrow mb-2 block">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-10 border-slate-800 bg-[#131927]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="border-slate-800 bg-[#131927]">
        <SelectItem value="all">All</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const FilterPanel = ({ filters, setFilters, meta }) => {
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v, ...(k === "sport" ? { position: "all", class_year: "all", division: "all", position_group: "all", attrs: [] } : {}) }));
  const positions = filters.sport === "all"
    ? [...new Set(Object.keys(SPORTS).flatMap((s) => meta.positions?.[s] || []))]
    : meta.positions?.[filters.sport] || [];
  const classYears = filters.sport === "all"
    ? [...new Set(Object.keys(SPORTS).flatMap((s) => meta.class_years?.[s] || []))].sort()
    : meta.class_years?.[filters.sport] || [];
  return (
    <div className="space-y-7">
      <div>
        <span className="eyebrow mb-2 block">Sport</span>
        <div className="grid grid-cols-2 gap-1 rounded-lg border border-slate-800 bg-[#131927] p-1">
          {SPORT_OPTIONS.map(([v, l]) => (
            <button key={v} data-testid={`filter-sport-${v}`} onClick={() => set("sport")(v)}
              className={`rounded-md py-2 font-data text-[11px] uppercase tracking-wider transition-colors duration-200 ${filters.sport === v ? "bg-[#FF2D78] text-white" : "text-slate-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <AttributeFilter sport={filters.sport} tags={meta.attributes?.[filters.sport]?.tags} season={meta.attributes?.[filters.sport]?.season}
        value={filters.attrs} onChange={set("attrs")} />
      {filters.sport === "rugby" && (<>
        <FilterSelect label="Division" value={filters.division} onChange={set("division")} options={meta.divisions?.rugby || []} testId="filter-division-select" />
        <FilterSelect label="Forwards / Backs" value={filters.position_group} onChange={set("position_group")} options={meta.position_groups?.rugby || []} testId="filter-position-group-select" />
      </>)}
      {filters.sport !== "beach" && <FilterSelect label="Position" value={filters.position} onChange={set("position")} options={positions} testId="filter-position-select" />}
      <FilterSelect label="Class year" value={filters.class_year} onChange={set("class_year")} options={classYears} testId="filter-class-year-select" />
      <FilterSelect label="Team" value={filters.team} onChange={set("team")} options={meta.teams || []} testId="filter-team-select" />
      <RangeFilter label="Age" testId="filter-age-range" value={filters.age} min={AGE_RANGE[0]} max={AGE_RANGE[1]} onChange={set("age")} />
      <RangeFilter label="Height" testId="filter-height-range" value={filters.height} min={HEIGHT_RANGE[0]} max={HEIGHT_RANGE[1]} onChange={set("height")} format={fmtHeight} />
      <button data-testid="filter-reset-button" onClick={() => setFilters(DEFAULT_FILTERS)}
        className="inline-flex items-center gap-2 font-data text-[11px] uppercase tracking-wider text-slate-400 transition-colors duration-200 hover:text-[#FF2D78]">
        <RotateCcw className="h-3.5 w-3.5" /> Reset filters
      </button>
    </div>
  );
};
